import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Col, Row } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import ButtonTooltip from "components/Common/ButtonTooltip"
import DeleteModal from "components/Common/DeleteModal"
import ConfirmModal from "components/Common/ConfirmModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"

import { useListParams } from "hooks/useListParams"
import {
  useAccountDutyPayments,
  useAccountDutyPayment,
  useDeleteAccountDutyPayment,
  useCompleteAccountDutyPayment,
} from "hooks/useAccountDutyPayment"
import { formatReadableDate } from "utils/formatDate"
import { downloadDutyPaymentPrint } from "api/accountDutyPaymentApi"

import AccountDutyPaymentAddEdit from "./AccountDutyPaymentAddEdit"
import AccountDutyPaymentView from "./AccountDutyPaymentView"

const STATUS_COLORS = {
  pending: "warning",
  completed: "success",
  cancelled: "secondary",
}

const formatDutyAmount = amount => {
  return `Tk. ${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0))}`
}

const AccountDutyPaymentList = () => {
  document.title = "Duty Payments"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedEditId, setSelectedEditId] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [completeModal, setCompleteModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [printLoadingId, setPrintLoadingId] = useState(null)
  const [statusFilter, setStatusFilter] = useState("")

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({
    page: 1,
    search: "",
  })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
      ...(statusFilter && { status: statusFilter }),
    }
  }, [params, statusFilter])

  const { data: responseData, isLoading, refetch, isRefetching } = useAccountDutyPayments(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountDutyPayment(selectedViewId, viewModal)
  const { data: editResponse, isLoading: isEditLoading } = useAccountDutyPayment(selectedEditId, modal && isEdit)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeleteAccountDutyPayment()
  const { mutateAsync: completeItem, isPending: completeLoading } = useCompleteAccountDutyPayment()

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedEditId(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedEditId(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedEditId(item?.id || null)
    setIsEdit(true)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleCompleteClick = item => {
    setSelectedData(item)
    setCompleteModal(true)
  }

  const handlePrint = async id => {
    try {
      setPrintLoadingId(id)
      await downloadDutyPaymentPrint(id)
    } catch {
      toast.error("Failed to download duty payment letter.")
    } finally {
      setPrintLoadingId(null)
    }
  }

  const handleComplete = async id => {
    try {
      const res = await completeItem(id)
      toast.success(res?.message ?? "Duty payment completed.")
      setCompleteModal(false)
      setSelectedData(null)
      refetch()
    } catch {
      toast.error("Failed to complete duty payment.")
    }
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Duty payment deleted.")
      setDeleteModal(false)
      setSelectedData(null)
      refetch()
    } catch {
      toast.error("Failed to delete duty payment.")
    }
  }

  const handleFilterReset = () => {
    setStatusFilter("")
  }

  const columns = useMemo(
    () => [
      {
        header: "Duty No",
        meta: { style: { minWidth: "160px" } },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <span className="font-monospace fw-semibold">{item.duty_payment_no || "-"}</span>
              <div className="d-sm-none small text-muted mt-1">
                {item.date ? formatReadableDate(item.date) : "-"}
              </div>
              <div className="d-sm-none mt-1">
                <div className="fw-semibold small font-monospace">{item.chassis_no || "-"}</div>
                <div className="text-muted" style={{ fontSize: "11px" }}>
                  {item.bill_of_entry_registration_no || ""}
                </div>
              </div>
              <div className="d-sm-none mt-1 d-flex align-items-center gap-2 flex-wrap">
                <Badge color="light" className="text-dark">
                  {formatDutyAmount(item.amount)}
                </Badge>
                <Badge color={STATUS_COLORS[item.status] || "secondary"} pill>
                  {item.status || "pending"}
                </Badge>
              </div>
            </div>
          )
        },
      },
      {
        header: "Date",
        meta: { style: { width: "12%" }, className: "d-none d-sm-table-cell" },
        cell: ({ row }) => row.original.date ? formatReadableDate(row.original.date) : "-",
      },
      {
        header: "Vehicle / BOE",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => (
          <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
            <div className="fw-semibold font-monospace">{row.original.chassis_no || "-"}</div>
            <div className="text-muted small">
              {row.original.bill_of_entry_registration_no || "-"}
              {row.original.registration_year ? ` - ${row.original.registration_year}` : ""}
            </div>
          </div>
        ),
      },
      {
        header: "Bank",
        meta: { className: "d-none d-md-table-cell" },
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.bank_name || "-"}</div>
            <div className="text-muted small">{row.original.branch_name || ""}</div>
          </div>
        ),
      },
      {
        header: "Amount",
        meta: { style: { width: "14%" }, className: "d-none d-md-table-cell" },
        cell: ({ row }) => (
          <Badge color="light" className="text-dark">
            {formatDutyAmount(row.original.amount)}
          </Badge>
        ),
      },
      {
        header: "Status",
        meta: { style: { width: "10%" }, className: "d-none d-sm-table-cell" },
        cell: ({ row }) => (
          <Badge color={STATUS_COLORS[row.original.status] || "secondary"} pill>
            {row.original.status || "pending"}
          </Badge>
        ),
      },
      {
        header: "Action",
        meta: { className: "table-col-action" },
        cell: ({ row }) => {
          const item = row.original
          const id = item.id
          return (
            <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
              <li>
                <ButtonTooltip type="view" btnId={`view-duty-${id}`} onClick={() => handleViewClick(item)} />
              </li>
              <li>
                <ButtonTooltip
                  type="download"
                  btnId={`print-duty-${id}`}
                  loading={printLoadingId === id}
                  onClick={() => handlePrint(id)}
                />
              </li>
              <CanView access={[ACCESS.ACCOUNT_DUTY_PAYMENT.COMPLETE]}>
                {item.status === "pending" && (
                  <li>
                    <ButtonTooltip
                      type="receipt"
                      btnId={`complete-duty-${id}`}
                      loading={completeLoading}
                      onClick={() => handleCompleteClick(item)}
                    />
                  </li>
                )}
              </CanView>
              <CanView access={[ACCESS.ACCOUNT_DUTY_PAYMENT.EDIT]}>
                <li>
                  <ButtonTooltip type="edit" btnId={`edit-duty-${id}`} onClick={() => handleEditClick(item)} />
                </li>
              </CanView>
              <CanView access={[ACCESS.ACCOUNT_DUTY_PAYMENT.DELETE]}>
                <li>
                  <ButtonTooltip
                    type="delete"
                    btnId={`delete-duty-${id}`}
                    loading={deleteLoading}
                    onClick={() => handleDeleteClick(item)}
                  />
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [printLoadingId, completeLoading, deleteLoading]
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data
  const hasStatusFilter = !!statusFilter

  return (
    <PageWrapper title="Duty Payments" breadcrumbItem="Duty Payment List">
      <Row className="mt-3 gy-2 align-items-center">
        <Col xs={6}>
          <h5 className="mb-0 card-title">
            Total <span className="badge badge-soft-info">{paginationInfo?.total ?? 0}</span>
          </h5>
        </Col>
        <Col xs={6} className="d-flex justify-content-end align-items-center gap-1">
          <ButtonTooltip
            type="refresh"
            btnId="refreshDutyPaymentList"
            size={null}
            loading={isRefetching}
            onClick={() => refetch()}
          />
          <CanView access={[ACCESS.ACCOUNT_DUTY_PAYMENT.ADD]}>
            <Button color="primary" onClick={handleAddNewClick}>
              <i className="bx bx-plus" />
              <span className="d-none d-sm-inline ms-1">Add New</span>
            </Button>
          </CanView>
          {(hasActiveFilters || hasStatusFilter) && (
            <Button color="danger" onClick={() => { handleFilterReset(); resetParams() }}>
              <span className="d-none d-sm-inline">Reset Filters</span>
              <i className="d-sm-none mdi mdi-filter-remove-outline" />
            </Button>
          )}
        </Col>
      </Row>

      <Row className="my-3 gy-2 align-items-center">
        <Col xs={12} sm={6} md={4}>
          <GlobalInputSearch
            value={params.search ?? ""}
            onChange={setSearch}
            placeholder="Search duty no, chassis, BOE, bank..."
          />
        </Col>
        <Col xs={12} sm={6} md={3}>
          <select
            className="form-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            aria-label="Status filter"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        tableClass="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
        customPageSize={params.per_page}
        getRowKey={row => row.id}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => {
          setDeleteModal(false)
          setSelectedData(null)
        }}
      />

      <ConfirmModal
        isOpen={completeModal}
        toggle={() => {
          setCompleteModal(false)
          setSelectedData(null)
        }}
        title="Mark Duty Payment as Completed"
        message={
          selectedData
            ? `Are you sure you want to mark duty payment ${selectedData.duty_payment_no || `#${selectedData.id}`} as completed? This will post a debit entry to the ledger.`
            : ""
        }
        confirmLabel="Mark Complete"
        btnColor="success"
        icon="mdi mdi-check-circle-outline"
        onConfirm={() => handleComplete(selectedData?.id)}
        isConfirming={completeLoading}
      />

      <AccountDutyPaymentView
        modal={viewModal}
        toggleModal={toggleViewModal}
        dutyPayment={viewResponse?.data}
        loading={isViewLoading}
        onComplete={handleComplete}
        onPrint={handlePrint}
        printLoadingId={printLoadingId}
      />

      <AccountDutyPaymentAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={editResponse?.data}
        isFetchingEdit={isEditLoading}
        toggleModal={toggleModal}
        onSuccess={refetch}
      />
    </PageWrapper>
  )
}

export default AccountDutyPaymentList
